import React, {useState, useEffect} from "react";
import {
    Box, Container, Paper, Typography, Chip, Stack, LinearProgress,
    Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MainLayout from "../MainLoyout/MainLayout";
import Api from "../../Services/Api";

const typeColor = (t) => ({GDN: "info", FB: "success", EMPTY: "default", OTHER: "warning"}[t] || "default");

function Stat({n, label}) {
    return (
        <Paper variant="outlined" sx={{px: 2, py: 1, minWidth: 90}}>
            <Typography variant="h6" sx={{lineHeight: 1.2}}>{n}</Typography>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Paper>
    );
}

export default function RotatorsPanel() {
    const [rotators, setRotators] = useState(null);
    const [generated, setGenerated] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            const data = await Api.getRotators();
            if (!alive) return;
            if (data && Array.isArray(data.rotators)) {
                setRotators(data.rotators);
                setGenerated(data.generated || "");
            } else {
                setRotators([]);
            }
            setLoading(false);
        })();
        return () => { alive = false; };
    }, []);

    const list = rotators || [];
    const active = list.filter((r) => r.count > 0).length;
    const totalUrls = list.reduce((s, r) => s + (r.count || 0), 0);
    const gdn = list.filter((r) => r.type === "GDN").length;
    const fb = list.filter((r) => r.type === "FB").length;

    return (
        <MainLayout title="Rotadores">
            <Container maxWidth="lg" sx={{py: 3}}>
                <Typography variant="h5" gutterBottom>🔀 Rotadores</Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Solo lectura · <code>config/rotators.php</code> + <code>rotators_gdn.php</code>
                    {generated ? ` · ${generated}` : ""}
                </Typography>

                {loading && <LinearProgress sx={{mb: 2}} />}

                {!loading && (
                    <Stack direction="row" spacing={1.5} sx={{mb: 3}} flexWrap="wrap" useFlexGap>
                        <Stat n={list.length} label="rotadores" />
                        <Stat n={active} label="activos" />
                        <Stat n={list.length - active} label="vacíos" />
                        <Stat n={gdn} label="GDN" />
                        <Stat n={fb} label="FB / RSOC" />
                        <Stat n={totalUrls} label="URLs" />
                    </Stack>
                )}

                {list.map((r) => (
                    <Paper key={r.id} variant="outlined" sx={{p: 2, mb: 1.5, opacity: r.count === 0 ? 0.6 : 1}}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{mb: 1}}>
                            <Typography variant="h6" sx={{fontFamily: "monospace"}}>{r.route}</Typography>
                            <Chip size="small" color={typeColor(r.type)} label={r.type} />
                            <Typography variant="body2" color="text.secondary" sx={{fontFamily: "monospace"}}>
                                {r.hosts && r.hosts.length ? r.hosts.join(", ") : "—"}
                            </Typography>
                        </Stack>

                        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                            {r.count} URLs · {(r.keywords || []).length} keywords
                            {(r.campaigns || []).length ? ` · ${r.campaigns.length} campañas` : ""}
                            {" · "}{r.source === "gdn" ? "rotators_gdn.php" : "rotators.php"}
                        </Typography>

                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{mb: 1}}>
                            {r.type === "GDN" && <Chip size="small" variant="outlined" label="campaña random" />}
                            {r.placement && <Chip size="small" variant="outlined" label="placement web ponderado" />}
                            {r.type === "GDN" && <Chip size="small" variant="outlined" label="gclid stampeado" />}
                            {r.type !== "GDN" && r.count > 0 && <Chip size="small" variant="outlined" label="round-robin" />}
                            {r.fbclid && <Chip size="small" variant="outlined" label="fbclid random" />}
                            {r.count === 0 && <Chip size="small" variant="outlined" label="vacío → 404" />}
                        </Stack>

                        {((r.supplies || []).length > 0 || (r.demands || []).length > 0) && (
                            <Box sx={{mb: 1, p: 1, bgcolor: "action.hover", borderRadius: 1}}>
                                {(r.supplies || []).length > 0 && (
                                    <Typography variant="body2" sx={{mb: (r.demands || []).length ? 0.5 : 0}}>
                                        <b>Supply:</b>{" "}
                                        {r.supplies.map((s) => `${s.name}${s.tag ? ` (${s.tag})` : ""} · sf${s.id}`).join("   ·   ")}
                                    </Typography>
                                )}
                                {(r.demands || []).length > 0 && (
                                    <Typography variant="body2">
                                        <b>Demand:</b>{" "}
                                        {r.demands.map((d) => `${d.needle} · df${d.id}`).join("   ·   ")}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {(r.keywords || []).length > 0 && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{mb: 1}}>
                                {r.keywords.slice(0, 30).map((k, i) => <Chip key={i} size="small" label={k} />)}
                                {r.keywords.length > 30 && <Chip size="small" label={`+${r.keywords.length - 30}`} />}
                            </Stack>
                        )}

                        {r.count > 0 && (
                            <Accordion disableGutters elevation={0} sx={{bgcolor: "transparent", "&:before": {display: "none"}}}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{px: 0, minHeight: 0}}>
                                    <Typography variant="caption" color="text.secondary">Ver las {r.count} URLs</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{px: 0}}>
                                    <Box sx={{maxHeight: 280, overflow: "auto", bgcolor: "action.hover", borderRadius: 1, p: 1}}>
                                        {(r.urls || []).map((u, i) => (
                                            <Typography key={i} variant="caption" component="div"
                                                        sx={{fontFamily: "monospace", wordBreak: "break-all", py: 0.25}}>
                                                {u}
                                            </Typography>
                                        ))}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </Paper>
                ))}

                {!loading && list.length === 0 && (
                    <Typography color="text.secondary">No se pudieron cargar los rotadores.</Typography>
                )}
            </Container>
        </MainLayout>
    );
}
